/* eslint-disable react/prop-types */
import { useState } from 'react';
import { useFetchEnergyConsumption } from './Hook/Index';
import {
  Table,
  Row,
  Col,
  Card,
  Empty,
  Alert,
  Tooltip,
  Button,
  Grid,
} from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { downloadExcel } from '../../utils/downloadExcel';
import { DownloadOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import { useOperatorStore } from '../../store/useOperatorStore';
import SharedFilters from './SharedFilters';
import { useAuthStore } from '../../store/useAuthStore';
import ReportTrendChart from './ReportTrendChart';

const { useBreakpoint } = Grid;

export default function EnergyConsumptionTab({ operatorId, active = false }) {
  const { t } = useTranslation('reportPage');
  const { name } = useOperatorStore();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ]);
  const [rfidFilter, setRfidFilter] = useState('ALL'); // Default to showing all data
  const [chargerId, setChargerId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const { user } = useAuthStore();

  const { data, isLoading } = useFetchEnergyConsumption(
    {
      operatorId,
      fromDate: dateRange[0],
      toDate: dateRange[1],
      locationId,
      chargerId,
      isRFID:
        rfidFilter === 'ALL' ? undefined : rfidFilter === 'RFID' ? true : false,
    },
    { enabled: active }
  );

  const handleExportCsv = async () => {
    const rows = Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : data?.totalEnergy || [];
    const csvData = rows.length
      ? rows.map((record) => ({
          OperatorName: record.operatorName,
          TotalEnergyKWh: (record.totalWh / 1000).toFixed(4),
        }))
      : (data?.series || []).map((record) => ({
          Date: record.date,
          TotalEnergyKWh: ((record.totalWh || 0) / 1000).toFixed(4),
        }));
    if (!csvData.length) return;
    await downloadExcel(csvData, 'EnergyConsumption.xlsx');
  };

  const columns = [
    {
      title: t('operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      align: 'center',
      width: isMobile ? 150 : 200,
    },
    {
      title: t('totalEnergy'),
      dataIndex: 'totalWh',
      key: 'totalWh',
      align: 'center',
      width: isMobile ? 120 : 150,
      render: (value) => `${(value / 1000).toFixed(4)} kWh`, // Convert to kWh
    },
  ];

  const energyItems = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : data?.totalEnergy || [];

  const totalEnergy = operatorId
    ? (data?.totalEnergy?.[0]?.totalWh || energyItems[0]?.totalWh || 0) / 1000
    : energyItems.reduce((sum, record) => sum + (record.totalWh || 0), 0) / 1000 || 0;

  const energySeries = (data?.series || []).map((row) => ({
    ...row,
    totalKwh: (row.totalWh || 0) / 1000,
  }));

  const handleFiltersChange = (filters) => {
    setDateRange(filters.dateRange);
    setRfidFilter(filters.rfidFilter);
    setChargerId(filters.chargerId);
    setLocationId(filters.locationId);
  };

  return (
    <div>
      {operatorId &&   user?.role === 'ADMIN' && (
        <Alert
          message={t('dashboard:alertMessage', {
            operatorName: name,
          })}
          type="info"
          showIcon
          closable
          style={{
            marginBottom: 24,
            border: 'none',
            backgroundColor: '#d7e1dd',
          }}
        />
      )}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4">
        <SharedFilters
          operatorId={operatorId}
          onFiltersChange={handleFiltersChange}
          onExport={handleExportCsv}
          isLoading={isLoading}
        />
       
      </div>
      <Row gutter={isMobile ? 8 : 16} className="mb-4">
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('totalEnergy')} loading={isLoading}>
            <div className="text-2xl text-center">
              {totalEnergy.toFixed(4)} kWh
            </div>
          </Card>
        </Col>
      </Row>
      <ReportTrendChart
        title={t('energyConsumptionTrend')}
        series={energySeries}
        loading={isLoading}
        yFormatter={(val) => `${val.toFixed(2)} kWh`}
        fields={[{ key: 'totalKwh', label: t('totalEnergy') }]}
      />
      {!operatorId && (
        <div className="overflow-x-auto mt-4">
          {energyItems.length > 0 ? (
            <Table
              columns={columns}
              dataSource={energyItems}
              loading={isLoading}
              rowKey="_id"
              pagination={false}
              scroll={{ x: isMobile ? 300 : undefined }}
            />
          ) : (
            !isLoading && <Empty description={t('noDataAvailable')} />
          )}
        </div>
      )}
    </div>
  );
}

EnergyConsumptionTab.propTypes = {
  active: PropTypes.bool.isRequired,
  operatorId: PropTypes.string,
};
